import apiService from './apiService.ts';

export interface GalleryResponse {
  tenantId: number;
  images: string[];
  count: number;
  maxAllowed?: number;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  fileName?: string;
  tenantId?: number;
}

class TenantMediaService {
  /** Upload menu PDF for a location (admin). Preview uses tenant public URL */
  async uploadMenu(locationId: number, file: File): Promise<UploadResponse> {
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('PDF file size exceeds 10MB limit');
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      throw new Error('File must be a PDF');
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiService.postFormData(
        `/api/v1/locations/${locationId}/menu`,
        formData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to upload menu');
    }
  }

  /** Upload gallery images for a location (admin). */
  async uploadGalleryImages(locationId: number, files: File[]): Promise<UploadResponse[]> {
    const results: UploadResponse[] = [];
    
    // Validate each file
    for (const file of files) {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        results.push({
          success: false,
          message: `${file.name}: File size exceeds 5MB limit`
        });
        continue;
      }

      // Check file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        results.push({
          success: false,
          message: `${file.name}: File must be JPG, JPEG, or PNG`
        });
        continue;
      }

      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await apiService.postFormData(
          `/api/v1/locations/${locationId}/images`,
          formData
        );
        results.push(response.data);
      } catch (error: any) {
        results.push({
          success: false,
          message: `${file.name}: ${error.response?.data?.error || 'Upload failed'}`
        });
      }
    }

    return results;
  }

  /**
   * Get gallery images list
   */
  async listGallery(tenantId: number): Promise<GalleryResponse> {
    try {
      // No dedicated admin endpoint yet; emulate using public list
      const response = await apiService.getPublic(`/public/tenants/${tenantId}/gallery`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      return { tenantId, images: [], count: 0, maxAllowed: 12 };
    }
  }

  /**
   * Get public gallery URLs (for preview)
   */
  async getPublicGallery(tenantId: number): Promise<GalleryResponse> {
    try {
      const response = await apiService.getPublic(`/public/tenants/${tenantId}/gallery`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch public gallery:', error);
      return { tenantId, images: [], count: 0 };
    }
  }

  /**
   * Delete gallery image
   */
  async deleteGalleryImage(_tenantId: number, _fileName: string): Promise<UploadResponse> {
    try {
      // Deleting by file name is not exposed; skip or implement when backend ready
      throw new Error('Delete not supported yet');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete image');
    }
  }

  /**
   * Check if tenant has menu
   */
  async hasMenu(tenantId: number): Promise<boolean> {
    try {
      const response = await apiService.getPublic(`/public/tenants/${tenantId}/has-menu`);
      return response.data.hasMenu;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get menu URL for preview
   */
  getMenuUrl(tenantId: number): string {
    return `/api/v1/public/tenants/${tenantId}/menu`;
  }
}

export default new TenantMediaService();
